import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  TaskImageUrlsDocument,
  TaskImageUrlsQuery,
  TaskImageUrlsQueryVariables,
} from 'core/graphql';

/**
 * Подписанные inline-ссылки на картинки задач (ТЗ 0109): обложки карточек
 * доски и миниатюры в окне задачи.
 *
 * Правила:
 *
 * 1. **Пачками ≤ 50** — столько сервер принимает за вызов.
 * 2. **Свой кеш по id, а НЕ кеш Apollo.** Ссылка — bearer-токен на пять минут:
 *    в `ROOT_QUERY` она оседала бы в дампах состояния (тот же довод, что у
 *    `useTaskAttachmentUrl`). Кеш общий для всех экранов модуля.
 * 3. **Свежесть считается от МОМЕНТА ПОЛУЧЕНИЯ, а не от часов сервера**
 *    (ревью 0109). `expiresAt` — время сервера; у ноутбука, чьи часы ушли
 *    вперёд на пять минут, каждая ссылка «протухала» в момент прихода, и хук
 *    перезапрашивал её по кругу. Теперь срок жизни зажат между 60 с и 4,5 мин
 *    от прихода, а один id спрашивается не чаще раза в 30 с — что бы ни
 *    говорили часы.
 * 4. **Старая ссылка показывается, пока не пришла новая** — картинка не
 *    мигает пустым местом в момент истечения.
 * 5. **Сетевой сбой — не отказ.** Повтор через 10 с, 20 с, 30 с; после трёх
 *    неудач id попадает в `failed`, и экран показывает «Не удалось загрузить»
 *    с кнопкой повтора вместо вечного «Загружается…». Чего сервер не отдал
 *    (не картинка, нет доступа) — тоже в `failed`, но без автоповтора.
 */

export const IMAGE_URL_BATCH = 50;
/** Запас до истечения по часам сервера. */
export const IMAGE_URL_SAFETY_MS = 30 * 1000;
/** Не меньше стольких мс ссылка считается свежей после прихода. */
export const IMAGE_URL_MIN_FRESH_MS = 60 * 1000;
/** И не больше: ссылка живёт пять минут, часы клиента могут отставать. */
export const IMAGE_URL_MAX_FRESH_MS = 270 * 1000;
/** Один id — не чаще раза в столько мс. */
export const IMAGE_URL_MIN_GAP_MS = 30 * 1000;
export const IMAGE_URL_RETRY_STEP_MS = 10 * 1000;
export const IMAGE_URL_MAX_ATTEMPTS = 3;

interface ICachedUrl {
  url: string;
  /** Момент (по местным часам), после которого ссылку перезапрашиваем. */
  staleAt: number;
}

interface IFailure {
  attempts: number;
  /** Когда можно повторить; `Infinity` — только руками. */
  retryAt: number;
}

const cache = new Map<string, ICachedUrl>();
const inflight = new Map<string, Promise<void>>();
const lastFetchAt = new Map<string, number>();
const failures = new Map<string, IFailure>();
/** Ссылки, на которых картинка уже раз не загрузилась (`<img onError>`). */
const broken = new Set<string>();
/** Подписчики: смена кеша перерисовывает все экраны с этими id. */
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

/** Только для тестов: начать с чистого кеша. */
export const resetTaskImageUrlCache = (): void => {
  cache.clear();
  inflight.clear();
  lastFetchAt.clear();
  failures.clear();
  broken.clear();
};

export const chunkIds = (ids: string[], size = IMAGE_URL_BATCH): string[][] => {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
};

/** Срок свежести ссылки, полученной в `now`, по `expiresAt` сервера. */
export const staleAtFor = (expiresAt: string, now: number): number => {
  const expires = Date.parse(expiresAt);
  const byServer = Number.isNaN(expires)
    ? now + IMAGE_URL_MAX_FRESH_MS
    : expires - IMAGE_URL_SAFETY_MS;
  return Math.min(
    now + IMAGE_URL_MAX_FRESH_MS,
    Math.max(now + IMAGE_URL_MIN_FRESH_MS, byServer),
  );
};

const isFailed = (id: string): boolean => {
  const f = failures.get(id);
  return Boolean(f && f.retryAt === Infinity);
};

/** Нужен ли запрос по id прямо сейчас. */
const due = (id: string, now: number): boolean => {
  if (inflight.has(id)) return false;
  const f = failures.get(id);
  if (f && f.retryAt > now) return false;
  const hit = cache.get(id);
  if (hit && hit.staleAt > now) return false;
  const last = lastFetchAt.get(id);
  return last === undefined || now - last >= IMAGE_URL_MIN_GAP_MS;
};

/** Ближайший момент, когда по одному из id понадобится запрос. */
const nextWake = (ids: string[], now: number): number | undefined => {
  const times: number[] = [];
  ids.forEach((id) => {
    const hit = cache.get(id);
    const f = failures.get(id);
    const last = lastFetchAt.get(id);
    const gapEnd = last === undefined ? now : last + IMAGE_URL_MIN_GAP_MS;
    if (f && f.retryAt !== Infinity) times.push(Math.max(f.retryAt, gapEnd));
    else if (hit) times.push(Math.max(hit.staleAt, gapEnd));
  });
  const future = times.filter((at) => at > now).sort((a, b) => a - b);
  return future[0];
};

export interface ITaskImageUrls {
  /** `{ id: url }` — в том числе устаревшая ссылка, пока не пришла новая. */
  urls: Record<string, string>;
  /** Не удалось загрузить (сеть после трёх попыток или отказ сервера). */
  failed: ReadonlySet<string>;
  /** «Повторить» — снять отказ и спросить заново. */
  retry: (id: string) => void;
  /**
   * Картинка по ссылке не загрузилась (`<img onError>`): ссылку выбросить и
   * спросить свежую — ОДИН раз. Второй сбой той же картинки — `failed`, а
   * не бесконечный круг «битая ссылка → новая → снова битая».
   */
  invalidate: (id: string) => void;
}

/**
 * Ссылки на картинки по id вложений.
 */
export const useTaskImageUrls = (
  ids: ReadonlyArray<string | null | undefined>,
): ITaskImageUrls => {
  const client = useApolloClient();
  // Ключ-строка вместо массива: новая ссылка на массив каждый рендер рвала
  // бы эффект и устраивала бесконечные запросы.
  const key = useMemo(
    () =>
      Array.from(new Set(ids.filter((id): id is string => Boolean(id))))
        .sort()
        .join(','),
    [ids],
  );
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    const bump = () => {
      if (alive.current) setTick((n) => n + 1);
    };
    listeners.add(bump);
    return () => {
      alive.current = false;
      listeners.delete(bump);
    };
  }, []);

  useEffect(() => {
    const wanted = key ? key.split(',') : [];
    if (!wanted.length) return undefined;
    const now = Date.now();
    const missing = wanted.filter((id) => due(id, now));

    chunkIds(missing).forEach((batch) => {
      batch.forEach((id) => lastFetchAt.set(id, now));
      const job = client
        .query<TaskImageUrlsQuery, TaskImageUrlsQueryVariables>({
          query: TaskImageUrlsDocument,
          variables: { attachmentIds: batch },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          const arrived = Date.now();
          const got = new Set<string>();
          (res.data?.staffTaskImageUrls || []).forEach((row) => {
            got.add(row.id);
            cache.set(row.id, {
              url: row.url,
              staleAt: staleAtFor(row.expiresAt, arrived),
            });
            failures.delete(row.id);
          });
          // Чего сервер не отдал — не картинка или нет доступа: повтор
          // ничего не изменит, только руками.
          batch.forEach((id) => {
            if (!got.has(id))
              failures.set(id, { attempts: 0, retryAt: Infinity });
          });
        })
        .catch(() => {
          const failedAt = Date.now();
          batch.forEach((id) => {
            const attempts = (failures.get(id)?.attempts || 0) + 1;
            failures.set(id, {
              attempts,
              retryAt:
                attempts >= IMAGE_URL_MAX_ATTEMPTS
                  ? Infinity
                  : failedAt + IMAGE_URL_RETRY_STEP_MS * attempts,
            });
          });
        })
        .finally(() => {
          batch.forEach((id) => inflight.delete(id));
          notify();
        });
      batch.forEach((id) => inflight.set(id, job));
    });

    const wake = nextWake(wanted, now);
    const timer =
      wake !== undefined
        ? window.setTimeout(() => {
            if (alive.current) setTick((n) => n + 1);
          }, Math.max(1000, wake - now))
        : undefined;
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [key, tick, client]);

  const retry = useCallback((id: string) => {
    failures.delete(id);
    lastFetchAt.delete(id);
    broken.delete(id);
    notify();
  }, []);

  const invalidate = useCallback((id: string) => {
    cache.delete(id);
    if (broken.has(id)) {
      failures.set(id, { attempts: 0, retryAt: Infinity });
    } else {
      broken.add(id);
      failures.delete(id);
      lastFetchAt.delete(id);
    }
    notify();
  }, []);

  const result = useMemo(() => {
    const urls: Record<string, string> = {};
    const failed = new Set<string>();
    (key ? key.split(',') : []).forEach((id) => {
      const hit = cache.get(id);
      if (hit) urls[id] = hit.url;
      else if (isFailed(id)) failed.add(id);
    });
    return { urls, failed };
    // `tick` — сигнал «кеш модуля изменился».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);

  return { urls: result.urls, failed: result.failed, retry, invalidate };
};
